/**
 * Field Mapping Configuration
 * Maps database column names to PDF field placeholders
 */

import { toDevanagariNumerals } from "../../utils/devanagariNumerals";
import { isOfflineTentativeTypeValue } from "../../config/phaseConstants";
import { LONG_APPLICATION_TYPE_LABELS } from "../../config/applicationTypeLabels";
import { roadWidthText } from "../../config/roadWidthOptions";

/**
 * Source db column keys (as passed into getFieldValue below, not the target
 * field names) whose value is a plain number/measurement read in the
 * document's own Marathi digits (matching the Demand Note's convention) -
 * as opposed to an identifier like applicationId or mobile_number, which
 * stay in Arabic numerals per instruction.
 */
const NUMERIC_FIELDS_IN_DEVANAGARI = new Set([
  "plot_no",
  "khasara_no",
  "area_of_plot",
  "area_as_per_7_12",
  "area_as_per_revenue_record",
  "area_as_per_sale_deed",
  "area_under_possession",
  "mobile_no",
]);

export interface FormFieldMapping {
  plot_owner_name: string;
  layout_name: string;
  mauza: string;
  plot_number: string;
  khasara_number: string;
  area_of_plot: string;
  unit_area_of_plot: string;
  is_there_construction: string;
  construction_area: string;
  court_case: string;
  mobile_number: string;
  email_id: string;
  rl_application_type: string;
  address: string;
  road_leading_to_land: string;
  plot_usage_dp: string;
  area_as_per_7_12: string;
  area_as_per_revenue_record: string;
  area_as_per_sale_deed: string;
  area_under_possession: string;
}

/**
 * CORRECT FIELD MAPPING - Based on actual database columns
 * These are the snake_case column names from your database
 */
export const DEFAULT_FIELD_MAPPING: FormFieldMapping = {
  plot_owner_name: "plot_owner_s_name",           // Maps to: Arpita R Vishwakarma
  layout_name: "unauthorized_layout_name",         // Maps to: Beltarodi
  mauza: "mauja",                                  // Maps to: Pardi
  plot_number: "plot_no",                          // Maps to: 45
  khasara_number: "khasara_no",                    // Maps to: 33
  area_of_plot: "area_of_plot",                    // Maps to: 500
  unit_area_of_plot: "unit_area_of_plot",           // Maps to: sq. mt. / sq. ft.
  is_there_construction: "is_there_a_construction", // Maps to: false
  construction_area: "construction_area",          // Maps to: 500
  court_case: "is_there_a_court_case_going_on_regarding_the_plot", // Maps to: false
  mobile_number: "mobile_no",                      // Maps to: 7888898654
  email_id: "email_id",                            // Maps to: admin@gmail.com
  rl_application_type: "rl_application_type",       // Maps to: Temporary / Permanent
  address: "address",                               // Maps to: applicant's address
  road_leading_to_land: "road_leading_to_land",     // Maps to: 6 m / 7.5 m / 9 m / > 9 m (older applications: Yes / No or a typed width)
  plot_usage_dp: "plot_usage_dp",                   // Maps to: plot usage as per Development Plan
  area_as_per_7_12: "area_as_per_7_12",
  area_as_per_revenue_record: "area_as_per_revenue_record",
  area_as_per_sale_deed: "area_as_per_sale_deed",
  area_under_possession: "area_under_possession",
};

/**
 * Extracts and maps field values from raw application data
 */
export function extractFieldValues(rawData: Record<string, any>): Record<string, string> {
  const mapping = DEFAULT_FIELD_MAPPING;
  const extracted: Record<string, string> = {};

  // Map each field using the defined mapping
  Object.keys(mapping).forEach((targetField) => {
    const sourceField = mapping[targetField as keyof FormFieldMapping];
    const value = getFieldValue(rawData, sourceField);
    extracted[targetField] = value;
  });

  return extracted;
}

/**
 * Gets a field value from the data object, handling various data types
 */
export function getFieldValue(data: Record<string, any>, fieldKey: string): string {
  let value = data[fieldKey];

  if ((value === null || value === undefined || value === "") && fieldKey === "mauja") {
    value = data.village_mouje_name || data.mauja;
  }

  // Handle null/undefined or NaN
  if (value === null || value === undefined || Number.isNaN(value) || String(value) === "NaN") {
    return "";
  }

  if (fieldKey === "mauja" || fieldKey === "village_mouje_name") {
    const valStr = String(value).trim();
    if (valStr === "1" || valStr.toLowerCase() === "kharbi") return "Kharbi";
    if (valStr === "2" || valStr.toLowerCase() === "bahadura") return "Bahadura";
    if (valStr === "3" || valStr.toLowerCase() === "gonhi") return "Gonhi";
    return valStr;
  }

  // Handle boolean values - convert to Marathi Yes/No
  if (typeof value === "boolean") {
    return value ? "होय" : "नाही";
  }

  // road_leading_to_land is stored as the string "Yes"/"No" rather than a
  // real boolean, so it doesn't hit the boolean branch above - convert it
  // the same way for consistency with the rest of this Marathi document.
  if (fieldKey === "road_leading_to_land") {
    /**
     * Before the application is saved the dropdown still holds the option
     * id ("9980"), so it is turned into its width ("6 m") first.
     * BEFORE:
     * const valStr = String(value).trim().toLowerCase();
     * if (valStr === "yes") return "होय";
     * if (valStr === "no") return "नाही";
     * return String(value);
     */
    const roadText = roadWidthText(value);
    const valStr = roadText.toLowerCase();
    if (valStr === "yes") return "होय";
    if (valStr === "no") return "नाही";
    return roadText;
  }

  if (fieldKey === "rl_application_type") {
    // rl_application_type is a configurable-select field, so at form-fill
    // time it can still be the raw numeric option id (9993/9994, see
    // RL_APPLICATION_TYPE_OPTION_ID in phaseConstants.ts) rather than the
    // resolved "Permanent"/"Temporary" string cp_applications ends up
    // storing - handle both shapes the same way RenderField.tsx does.
    const valStr = String(value).trim().toLowerCase();
    /**
     * Item ५ prints Permanent / Tentative by their long names, as on the form
     * and the application details (config/applicationTypeLabels.ts).
     * BEFORE:
     * if (valStr === "permanent" || valStr === "9993") return "कायमस्वरूपी";
     * if (valStr === "temporary" || valStr === "9994") return "तात्पुरते";
     */
    if (valStr === "permanent" || valStr === "9993") return LONG_APPLICATION_TYPE_LABELS.PERMANENT;
    if (valStr === "temporary" || valStr === "9994") return LONG_APPLICATION_TYPE_LABELS.TENTATIVE;
    /**
     * BEFORE - Offline Tentative had no label of its own, so the printed form
     * would have shown the raw option id (9990).
     */
    if (isOfflineTentativeTypeValue(value)) return "ऑफलाइन तात्पुरते";
    return String(value);
  }

  // Handle objects (nested data)
  if (typeof value === "object" && !Array.isArray(value)) {
    // If object has a 'value' property, use that
    if (value.value !== undefined) {
      return String(value.value);
    }
    // If object has a 'name' property, use that
    if (value.name !== undefined) {
      return String(value.name);
    }
    // Otherwise return empty
    return "";
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (NUMERIC_FIELDS_IN_DEVANAGARI.has(fieldKey)) {
    return toDevanagariNumerals(value);
  }

  // Handle numbers and strings
  return String(value);
}
